import React, { useEffect, useState } from 'react';

const SyringeVisualization = ({ units, maxUnits, syringeSize }) => {
  const [animated, setAnimated] = useState(false);
  
  useEffect(() => {
    setAnimated(false);
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, [units, maxUnits]);

  // Calculate fill percentage (capped at 100%)
  const fillPercentage = Math.min((units / maxUnits) * 100, 100);
  
  // Generate tick marks based on syringe size
  const generateTicks = () => {
    const ticks = [];
    const totalTicks = maxUnits;
    const majorTickInterval = maxUnits === 30 ? 10 : maxUnits === 50 ? 10 : 10;
    
    // We'll show major ticks at 0, 10, 20, 30 (for 0.3mL) etc
    for (let i = 0; i <= totalTicks; i += majorTickInterval) {
      ticks.push({
        position: (i / totalTicks) * 100,
        label: i,
        isMajor: true
      });
    }
    
    return ticks;
  };

  const ticks = generateTicks();
  
  // Determine indicator position
  const indicatorPosition = Math.min(fillPercentage, 100);
  
  return (
    <div className="w-full max-w-md mx-auto">
      {/* Result Number */}
      <div className="text-center mb-4">
        <span className="text-5xl sm:text-6xl font-bold text-primary animate-pulse-glow inline-block px-4 py-2 rounded-lg">
          {units}
        </span>
        <span className="text-xl text-muted-foreground ml-2">units</span>
      </div>
      
      {/* Syringe Container */}
      <div className="relative bg-muted/30 rounded-lg p-6">
        {/* Syringe Body */}
        <div className="relative h-12 sm:h-14 bg-gradient-to-r from-muted to-muted/80 rounded-lg border-2 border-border overflow-hidden shadow-inner">
          {/* Fill Level */}
          <div 
            className={`absolute left-0 top-0 bottom-0 bg-gradient-to-r from-secondary via-secondary/90 to-secondary/70 rounded-l-md transition-all duration-700 ease-out ${animated ? '' : 'w-0'}`}
            style={{ 
              width: animated ? `${fillPercentage}%` : '0%',
            }}
          >
            {/* Liquid shine effect */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
          </div>
          
          {/* Tick Marks */}
          <div className="absolute inset-0 flex items-end justify-between pointer-events-none">
            {ticks.map((tick, index) => (
              <div 
                key={index}
                className="relative flex flex-col items-center"
                style={{ 
                  position: 'absolute',
                  left: `${tick.position}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div 
                  className={`w-0.5 bg-foreground/60 ${tick.isMajor ? 'h-4' : 'h-2'}`}
                />
              </div>
            ))}
          </div>
          
          {/* Current Position Indicator */}
          {units > 0 && (
            <div 
              className={`absolute top-0 bottom-0 w-1 bg-primary shadow-glow transition-all duration-700 ease-out ${animated ? '' : 'left-0'}`}
              style={{ 
                left: animated ? `${indicatorPosition}%` : '0%',
              }}
            >
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-primary" />
            </div>
          )}
        </div>
        
        {/* Scale Labels */}
        <div className="relative h-6 mt-2">
          {ticks.map((tick, index) => (
            <span
              key={index}
              className="absolute text-xs sm:text-sm font-medium text-muted-foreground transform -translate-x-1/2"
              style={{ left: `${tick.position}%` }}
            >
              {tick.label}
            </span>
          ))}
        </div>
        
        {/* Syringe Size Label */}
        <div className="text-center mt-4">
          <span className="inline-block bg-primary/20 text-primary px-3 py-1 rounded-full text-sm font-medium">
            {syringeSize} mL Syringe ({maxUnits} units)
          </span>
        </div>
      </div>
    </div>
  );
};

export default SyringeVisualization;
